/**
 * TypeScript shapes for the generation review API (AB#955377, RFC-017 3.4/4).
 *
 * These mirror `app/generation_api/src/models/{request,response}/review.py` and
 * the shared enums in `models/enums.py`. The backend serialises responses in
 * camelCase (pydantic `serialization_alias`), so these interfaces use camelCase.
 *
 * Kept hand-written for now. When `@xplatform/api-types` gains the generation
 * OpenAPI spec (work-plan "out of scope, later"), regenerate these from it.
 */

// ── Enums (member VALUES are the contract) ──────────────────────────────────

/**
 * The run's pipeline status (contract VERSION 36, D20). `GENERATED` = generation
 * finished and the run waits for review and Publish; `PUBLISHING` = the return
 * machine is running; `PROCESSING` = a regenerate is running.
 */
export type RunStatus =
  | "CREATED"
  | "QUEUED"
  | "PROCESSING"
  | "GENERATED"
  | "PUBLISHING"
  | "COMPLETED"
  | "REJECTED"
  | "FAILED"
  | "EXPIRED";

export type ImageStatus =
  | "PENDING"
  | "REDRAW"
  | "GUIDING"
  | "GUIDED"
  | "GENERATING"
  | "GENERATED"
  | "SUCCEEDED"
  | "FAILED";

export type ImageOrigin = "redrawn" | "reused";

/** RFC-011 `reason_lov` — the failure reason on a FAILED image. */
export type ReasonLov =
  | "source_unreadable"
  | "source_missing"
  | "gate_failed"
  | "policy_skip"
  | "review_rejected"
  | "generation_failed";

export type ReviewState = "pending" | "approved" | "rejected";

export type ReviewerType = "human" | "llm";

/** The verb the decision route accepts (maps to approved/rejected server-side). */
export type ReviewDecisionVerb = "approve" | "reject";

// ── Responses ───────────────────────────────────────────────────────────────

/** One row of `GET /review/runs`: a run in `GENERATED` (awaiting review). */
export interface ReviewRunSummary {
  runId: string;
  status: RunStatus;
  manualCount: number;
  imageCount: number;
  createdAt: string;
  updatedAt: string;
}

/** The generic paginated envelope (`models/pagination.Page`). */
export interface Page<T> {
  items: T[];
  /** Opaque sort-key cursor for the next page; null/absent on the last page. */
  nextCursor?: string | null;
}

/**
 * Per-`reviewState` counts computed on read. NOT the run's pipeline
 * `imageCounts` (where a rejected image still counts as succeeded).
 *
 * `failed` is the images that ended FAILED (any reason), and `regenerable` the
 * subset an admin may retry (FAILED / generation_failed while the run is
 * GENERATED). Failed images carry no reviewState, so they do not move the three
 * reviewState counts.
 */
export interface ReviewCounts {
  pending: number;
  approved: number;
  rejected: number;
  notApplicable: number;
  failed: number;
  regenerable: number;
}

/** One row of `GET /review/runs/{runId}/images`.
 *
 * The list also carries images that ended FAILED. `reason` is the RFC-011 failure
 * reason (null on a non-failed image), and `regenerable` is true only for a
 * FAILED / generation_failed image on a GENERATED run. A failed image has no
 * `reviewState`. A reused image (`origin: "reused"`) is reviewed like a redrawn one. */
export interface ReviewImageSummary {
  requestId: string;
  objId: string;
  status: ImageStatus;
  reason?: ReasonLov | null;
  regenerable?: boolean;
  origin?: ImageOrigin | null;
  reviewState?: ReviewState | null;
  reviewScore?: number | null;
  reviewerType?: ReviewerType | null;
  reviewedAt?: string | null;
}

export interface ReviewImageListResponse {
  /** The run's pipeline status: drives whether Publish and Regenerate may be
   * acted on and which poll runs (regenerate or publish). */
  runStatus: RunStatus;
  counts: ReviewCounts;
  images: ReviewImageSummary[];
}

/**
 * `GET /review/runs/{runId}/images/{requestId}/{objId}`: source and redraw
 * presigned GETs, the quality-gate flag, origin, review state and the manual
 * context text the image was generated from.
 *
 * `distinctness` / `accuracy` carry a model value ONLY when the image has one
 * (our pipeline has none today, I-11). The two sliders render, and are sent,
 * only when the value is present.
 */
export interface ReviewImageDetail {
  requestId: string;
  objId: string;
  status: ImageStatus;
  origin?: ImageOrigin | null;
  reviewState?: ReviewState | null;
  gatesDegraded?: boolean | null;
  sourceUrl?: string | null;
  finalUrl?: string | null;
  context?: string | null;
  expiresIn: number;
  /** Optional model values; present only when the image carries them. */
  distinctness?: number | null;
  accuracy?: number | null;
}

/** `POST .../decision` -> 200: the state the image now carries. */
export interface ReviewDecisionResponse {
  requestId: string;
  objId: string;
  reviewState: ReviewState;
}

/** `POST /review/runs/{runId}/publish` -> 202: the return message was sent. */
export interface PublishResponse {
  runId: string;
  accepted: boolean;
}

/** `POST /review/runs/{runId}/regenerate` -> 202 (D17): the regenerate message
 * reached the starter. `regenId` names the regenerate execution; `count` is the
 * number of images requested. The API does NOT move the run or images. */
export interface RegenerateResponse {
  regenId: string;
  count: number;
}

// ── Requests ──────────────────────────────────────────────────────────────

/**
 * `POST /review/runs/{runId}/images/{requestId}/{objId}/decision`.
 *
 * `score` (1-5) is required on BOTH decisions; `reason` is required on reject.
 * `distinctness` / `accuracy` (0-100) are sent ONLY when the image carried a
 * model value. The reviewer identity is NEVER in the body — it comes from the
 * token. `reviewerType` defaults to `human`.
 */
export interface ReviewDecisionRequest {
  decision: ReviewDecisionVerb;
  score: number;
  reason?: string;
  distinctness?: number;
  accuracy?: number;
  reviewerType?: ReviewerType;
}

/** One image to regenerate, addressed by `(requestId, objId)`. */
export interface RegenerateImageRef {
  requestId: string;
  objId: string;
}

/**
 * `POST /review/runs/{runId}/regenerate`.
 *
 * 1-100 images. The requester identity is NEVER in the body — it comes from the
 * token server-side.
 */
export interface RegenerateRequest {
  images: RegenerateImageRef[];
}

// ── Error codes the review routes return (for ApiError.errorCode branching) ──

export const REVIEW_ERROR_CODES = {
  imageAlreadyDecided: "image_already_decided",
  reviewPending: "review_pending",
  runNotGenerated: "run_not_generated",
  imageNotRegenerable: "image_not_regenerable",
} as const;
